/**
 * Calendar API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  CalendarEvent,
  CalendarEventListResponse,
  CalendarEventResponse,
  CalendarEventCreateInput,
  CalendarEventUpdateInput,
  CalendarAttendee,
  CalendarAttendeeCreateInput,
} from '../types';

interface ListEventsOptions {
  skip?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  startDate?: string;
  endDate?: string;
  eventTypeId?: string;
  status?: string;
  cvId?: string;
  jobId?: string;
  memberId?: string;
}

class CalendarService {
  /**
   * List calendar events with filters.
   */
  async listEvents(
    workspaceId: string,
    idToken: string,
    options: ListEventsOptions = {}
  ): Promise<CalendarEventListResponse> {
    const {
      skip = 0,
      limit = 100,
      sortBy = 'start_time',
      order = 'asc',
      search,
      startDate,
      endDate,
      eventTypeId,
      status,
      cvId,
      jobId,
      memberId,
    } = options;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CalendarEventListResponse>('/calendar', {
      params: {
        skip,
        limit,
        sort_by: sortBy,
        order,
        search,
        start_date: startDate,
        end_date: endDate,
        event_type_id: eventTypeId,
        status,
        cv_id: cvId,
        job_id: jobId,
        member_id: memberId,
      },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list calendar events');
    }

    return response.data;
  }

  /**
   * Get a single calendar event by ID.
   */
  async getEvent(eventId: string, workspaceId: string, idToken: string): Promise<CalendarEvent> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CalendarEventResponse>(`/calendar/${eventId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get calendar event');
    }

    return response.data.data;
  }

  /**
   * Create a new calendar event.
   */
  async createEvent(
    workspaceId: string,
    idToken: string,
    data: CalendarEventCreateInput
  ): Promise<CalendarEvent> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<CalendarEventResponse>('/calendar', data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create calendar event');
    }

    return response.data.data;
  }

  /**
   * Update a calendar event.
   */
  async updateEvent(
    eventId: string,
    workspaceId: string,
    idToken: string,
    data: CalendarEventUpdateInput
  ): Promise<CalendarEvent> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<CalendarEventResponse>(`/calendar/${eventId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update calendar event');
    }

    return response.data.data;
  }

  /**
   * Delete a calendar event.
   */
  async deleteEvent(eventId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/calendar/${eventId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  /**
   * Add attendees to an event.
   */
  async addAttendees(
    eventId: string,
    workspaceId: string,
    idToken: string,
    attendees: CalendarAttendeeCreateInput[]
  ): Promise<CalendarAttendee[]> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{ success: boolean; data: CalendarAttendee[]; message?: string }>(
      `/calendar/${eventId}/attendees`,
      attendees,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add attendees');
    }

    return response.data.data;
  }

  /**
   * Remove an attendee from an event.
   */
  async removeAttendee(
    eventId: string,
    attendeeId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/calendar/${eventId}/attendees/${attendeeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const calendarService = new CalendarService();
export default calendarService;
